import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, FileUp, X, Download } from 'lucide-react';
import { Btn, Card, StatusBadge } from '../platform/ui';
import { toolsApi, saveBlob } from '../../services/toolsApi';

export const formatSize = (bytes) => {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const formatTime = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Выбор файла снимка: файл (ZIP, NIfTI, MHA) или папка DICOM. Передаёт наружу массив File. */
export const FilePicker = ({ label, files, onChange, disabled }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const dirRef = useRef(null);
  const total = files.reduce((sum, f) => sum + f.size, 0);
  const pick = (e) => { const list = Array.from(e.target.files || []); if (list.length) onChange(list); e.target.value = ''; };
  return (
    <div className="mb-5">
      {label && <div className="text-sm font-bold mb-2">{label}</div>}
      <div className="flex flex-wrap gap-3">
        <Btn variant="secondary" disabled={disabled} onClick={() => fileRef.current?.click()}><FileUp size={16} />{t('pl.toolsPage.common.chooseFile')}</Btn>
        <Btn variant="secondary" disabled={disabled} onClick={() => dirRef.current?.click()}><FolderOpen size={16} />{t('pl.toolsPage.common.chooseFolder')}</Btn>
      </div>
      <input ref={fileRef} type="file" className="hidden" accept=".zip,.nii,.gz,.mha,.mhd,.dcm" onChange={pick} />
      <input ref={dirRef} type="file" className="hidden" webkitdirectory="" directory="" multiple onChange={pick} />
      <p className="text-xs text-gray-500 mt-2">{t('pl.toolsPage.common.formats')}</p>
      {files.length > 0 && (
        <p className="mt-2 text-sm flex items-center gap-2" data-testid="files-chosen">
          {t('pl.toolsPage.common.filesChosen', { count: files.length, size: formatSize(total) })}
          <button type="button" className="underline inline-flex items-center gap-1" disabled={disabled} onClick={() => onChange([])}>
            <X size={14} />{t('pl.toolsPage.common.clear')}
          </button>
        </p>
      )}
    </div>
  );
};

/** Собирает FormData: файлы под именем поля и остальные параметры (пустые пропускаются). */
export const buildForm = (fields, params) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([name, files]) => files.forEach((f) => fd.append(name, f, f.name)));
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v); });
  return fd;
};

/** Загрузка / обработка / ошибка. Результат рисует вызывающая страница. */
export const TaskProgress = ({ task }) => {
  const { t } = useTranslation();
  if (task.phase === 'idle' || task.phase === 'done') return null;
  if (task.phase === 'error') {
    let text = task.error;
    if (task.error === 'timeout') text = t('pl.toolsPage.common.failed');
    return (
      <Card className="mb-6 border-red-700">
        <p className="font-bold text-red-800">{t('pl.toolsPage.common.failed')}</p>
        {text && text !== t('pl.toolsPage.common.failed') && <p className="mt-1 text-sm">{text}</p>}
        <Btn className="mt-4" variant="secondary" onClick={task.reset}>{t('pl.toolsPage.common.again')}</Btn>
      </Card>
    );
  }
  const uploading = task.phase === 'uploading';
  const pct = uploading ? task.uploadPct : task.progress;
  return (
    <Card className="mb-6" role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="font-bold">
          {uploading ? t('pl.toolsPage.common.uploading', { pct: task.uploadPct }) : (task.message || t('pl.toolsPage.common.processing'))}
        </span>
        {!uploading && <StatusBadge tone="blue">{pct}%</StatusBadge>}
      </div>
      <div className="h-3 border-2 border-black bg-white"><div className="h-full bg-[#C41E3A]" style={{ width: `${pct}%`, transition: 'width .4s' }} /></div>
      <div className="mt-2 text-sm text-gray-600 flex flex-wrap gap-x-4">
        {!uploading && <span>{t('pl.toolsPage.common.elapsed', { time: formatTime(task.elapsed) })}</span>}
        {task.queuePosition ? <span>{t('pl.toolsPage.common.queue', { n: task.queuePosition })}</span> : null}
      </div>
      {task.reconnecting && <p className="mt-3 text-sm text-amber-800">{t('pl.toolsPage.common.reconnecting')}</p>}
      {!uploading && <Btn className="mt-4" variant="secondary" onClick={task.cancel}>{t('pl.toolsPage.common.cancel')}</Btn>}
    </Card>
  );
};

/** Картинка результата: файл забирается с авторизацией и показывается через object URL. */
export const ResultImage = ({ taskId, name, alt, className = '', api = toolsApi }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url = null;
    let alive = true;
    setSrc(null);
    setFailed(false);
    api.download(taskId, name).then((blob) => {
      if (!alive) return;
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => alive && setFailed(true));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [taskId, name, api]);
  if (failed) return <div className="text-sm text-red-700">{alt}: —</div>;
  if (!src) return <div className={`animate-pulse bg-black/10 min-h-[160px] ${className}`} aria-label={alt} />;
  return <img src={src} alt={alt} className={className} />;
};

/** Кнопка скачивания файла результата. */
export const DownloadButton = ({ taskId, name, label, variant = 'secondary', api = toolsApi }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { saveBlob(await api.download(taskId, name), name); } finally { setBusy(false); }
  };
  return <Btn variant={variant} loading={busy} onClick={run}><Download size={16} />{label || name}</Btn>;
};

export const Slider = ({ label, value, onChange, min, max, step = 1, unit = '', disabled }) => (
  <label className="block mb-3">
    <span className="flex justify-between text-sm font-bold"><span>{label}</span><span>{value}{unit}</span></span>
    <input type="range" className="w-full" min={min} max={max} step={step} value={value} disabled={disabled}
           onChange={(e) => onChange(parseFloat(e.target.value))} />
  </label>
);
